
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NormalDistributionCalculator = () => {
  const [data, setData] = useState('');
  const [mean, setMean] = useState('');
  const [stdDev, setStdDev] = useState('');
  const [probability, setProbability] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setData(e.target.value);
    setError('');
  };

  const handleMeanChange = (e) => {
    setMean(e.target.value);
    setError('');
  };

  const handleStdDevChange = (e) => {
    setStdDev(e.target.value);
    setError('');
  };

  const calculateProbability = () => {
    const x = parseFloat(data);
    const mu = parseFloat(mean);
    const sigma = parseFloat(stdDev);

    if (isNaN(x) || isNaN(mu) || isNaN(sigma) || sigma === 0) {
      setError('Please enter valid numbers for data, mean, and standard deviation, and ensure the standard deviation is not zero.');
      return;
    }

    // Calculate Z-Score
    const z = (x - mu) / sigma;

    // Calculate probability using the cumulative distribution function (CDF)
    const cdf = (z) => {
      const t = 1 / (1 + 0.2316419 * Math.abs(z));
      const b1 = 0.319381530;
      const b2 = -0.356563782;
      const b3 = 1.781477937;
      const b4 = -1.821255978;
      const b5 = 1.330274429;
      const p = 1 - (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-z * z / 2) * 
        (b1 * t + b2 * t * t + b3 * Math.pow(t, 3) + b4 * Math.pow(t, 4) + b5 * Math.pow(t, 5));
      return z > 0 ? p : 1 - p;
    };

    const prob = cdf(z);
    const calculationSteps = [
      'Step 1: Prepare the values',
      `Data (x): ${x}`,
      `Mean (μ): ${mu}`,
      `Standard Deviation (σ): ${sigma}`,
      'Step 2: Calculate Z-Score',
      `Z-Score = (x - μ) / σ`,
      `Z-Score = (${x} - ${mu}) / ${sigma} = ${z.toFixed(2)}`,
      'Step 3: Calculate Probability',
      `Probability = CDF(Z-Score)`,
      `Probability = ${prob.toFixed(4)} (or ${prob * 100}%)`
    ];

    setProbability({
      value: prob.toFixed(4),
      zScore: z.toFixed(2),
      calculationSteps
    });

    // Generate chart data
    const generateNormalDistribution = (mu, sigma) => {
      const xValues = Array.from({ length: 100 }, (_, i) => 
        mu - 3 * sigma + (i * (6 * sigma) / 99)
      );
      return xValues.map(x => ({
        x,
        y: (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2))
      }));
    };

    const distributionData = generateNormalDistribution(mu, sigma);
    setChartData({
      labels: distributionData.map(d => d.x.toFixed(2)),
      datasets: [
        {
          label: 'Normal Distribution',
          data: distributionData.map(d => d.y),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 0
        },
        {
          label: 'Data Point',
          data: [0, 0],
          borderColor: 'rgb(255, 99, 132)',
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: 'rgb(255, 99, 132)',
          showLine: false
        }
      ]
    });
  };

  const exampleData = {
    x: 75,
    mu: 70,
    sigma: 5,
    z: 1.00,
    prob: 0.8413
  };

  return (
    <>
      <Helmet>
        <title>Normal Distribution Calculator - Statistics</title>
        <meta name="description" content="Calculate probabilities using the Normal Distribution. Use our interactive calculator to find probabilities based on mean and standard deviation." />
        <meta name="keywords" content="normal distribution calculator, statistics calculator, probability calculator" />
        <meta property="og:title" content="Normal Distribution Calculator - Statistics" />
        <meta property="og:description" content="Calculate probabilities using the Normal Distribution. Use our interactive calculator to find probabilities based on mean and standard deviation." />
        <meta name="twitter:title" content="Normal Distribution Calculator - Statistics" />
        <meta name="twitter:description" content="Calculate probabilities using the Normal Distribution. Use our interactive calculator to find probabilities based on mean and standard deviation." />
      </Helmet>

      <div className="statistics-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">📊</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Normal Distribution Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Statistics Calculator</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Calculate probabilities using the Normal Distribution. This calculator helps you find probabilities based on the mean and standard deviation of the data set.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="data">
              Data (x):
            </label>
            <input
              type="text"
              id="data"
              name="data"
              value={data}
              onChange={handleInputChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter the data point (x)"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mean">
              Mean (μ):
            </label>
            <input
              type="text"
              id="mean"
              name="mean"
              value={mean}
              onChange={handleMeanChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter the mean (μ)"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="stdDev">
              Standard Deviation (σ):
            </label>
            <input
              type="text"
              id="stdDev"
              name="stdDev"
              value={stdDev}
              onChange={handleStdDevChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter the standard deviation (σ)"
            />
          </div>

          <button
            onClick={calculateProbability}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate Probability
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {probability && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Probability</h4>
                  <div className="space-y-2">
                    <p className="flex justify-between">
                      <span>Probability (P(X ≤ x))</span>
                      <span className="font-medium">{probability.value}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Z-Score</span>
                      <span className="font-medium">{probability.zScore}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-12 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Normal Distribution</h4>
                  <div className="h-[400px]">
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            title: {
                              display: true,
                              text: 'X Values'
                            }
                          },
                          y: {
                            title: {
                              display: true,
                              text: 'Probability Density'
                            },
                            beginAtZero: true
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Normal Distribution'
                          },
                          legend: {
                            display: true,
                            position: 'bottom'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-4">
                  {probability.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Example Calculation <span className="ml-2 text-2xl">📝</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Here's an example calculation for a data point x = 75 with mean μ = 70 and standard deviation σ = 5:
            </p>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold mb-4">Sample Data:</h4>
              <ul className="list-disc pl-8 space-y-2">
                <li>Data (x): {exampleData.x}</li>
                <li>Mean (μ): {exampleData.mu}</li>
                <li>Standard Deviation (σ): {exampleData.sigma}</li>
                <li>Z-Score: {exampleData.z}</li>
                <li>Probability: {exampleData.prob}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Normal Distribution Calculator <span className="ml-2 text-2xl">📋</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Follow these steps to use the Normal Distribution Calculator effectively:
            </p>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Data</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Collect the data point you want to analyze</li>
                  <li>Ensure you know the mean (μ) and standard deviation (σ) of the data set</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Data</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Input the data point (x), mean (μ), and standard deviation (σ) into the calculator</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Review the calculated probability and Z-Score</li>
                  <li>Use the normal distribution chart for visual analysis</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-6">
              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is the Normal Distribution?</h3>
                <p className="text-gray-600">
                  The Normal Distribution, also known as the Gaussian Distribution, is a continuous probability distribution that is symmetric about the mean. It's characterized by its bell-shaped curve and is widely used in statistics and probability theory.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">How is the probability calculated?</h3>
                <p className="text-gray-600">
                  The probability is calculated using the cumulative distribution function (CDF) of the normal distribution. It gives the probability that a random variable from the distribution is less than or equal to a given value.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is the Z-Score in this context?</h3>
                <p className="text-gray-600">
                  The Z-Score represents how many standard deviations a data point is from the mean. It's calculated as (x - μ) / σ and is used to standardize the normal distribution.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features & Benefits <span className="ml-2 text-2xl">✨</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
                <p className="text-gray-600">Interactive normal distribution chart for easy visualization of probability calculations</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
                <p className="text-gray-600">Accurate probability calculations with step-by-step breakdown</p>
              </div>
              <div className="text-center p-6 bg-yellow-50 rounded-lg">
                <div className="text-4xl mb-4">🔄</div>
                <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
                <p className="text-gray-600">Automatic handling of data format conversions</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
                <p className="text-gray-600">Detailed explanations of normal distribution concepts</p>
              </div>
              <div className="text-center p-6 bg-pink-50 rounded-lg">
                <div className="text-4xl mb-4">⚙️</div>
                <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
                <p className="text-gray-600">Intuitive interface with clear input fields</p>
              </div>
              <div className="text-center p-6 bg-orange-50 rounded-lg">
                <div className="text-4xl mb-4">🔄</div>
                <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
                <p className="text-gray-600">Instant calculation results as you type</p>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Understanding Normal Distribution <span className="ml-2 text-2xl">📊</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              The Normal Distribution is a fundamental concept in statistics that helps us understand how data is distributed. Here's why it's important:
            </p>
            <ul className="list-none pl-8 space-y-4">
              <li className="flex items-center">
                <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
                <span className="font-medium">Data Analysis:</span> Understanding the distribution of your data points
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                <span className="font-medium">Probability Estimation:</span> Calculating probabilities for specific data points
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                <span className="font-medium">Standardization:</span> Converting data to Z-Scores for comparison
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
                <span className="font-medium">Statistical Inference:</span> Making predictions and decisions based on data
              </li>
            </ul>
            <p className="italic text-gray-500 mt-6">
              Note: Always double-check your data and calculations to ensure accuracy and avoid misleading results.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NormalDistributionCalculator;